<template>
	<div class="tw-mb-10">
		<valuation-layout>
			<valuation-model-stepper slot="stepper" :current-step="3" />
			<div slot="content">
				<degree-factor-navigation
					class="tw-mb-8"
					:selected-factors="selectedFactors"
					:selected-factor="selectedFactorDegree"
					:pg-max="selectedFactorPGM"
					:pg-min="selectedFactorPGm"
					:disable-next-button="!hasNext"
					:disable-prev-button="!hasPrev"
					@onPrevClick="goPrevFactor"
					@onNextClick="goNextFactor"
				/>
				<degree-table
					:degree-list="selectedFactorDegrees"
					:selected-degrees="selectedFactorSelectedDegrees"
					@degreeListChange="onSelectedFactorDegreesChange"
				/>
			</div>
			<degrees-description-navigation
				slot="description"
				:factor-degrees-by-factor="selectedFactorSelectedDegrees"
				:disable-create="!degreesAreCompleted"
				@createClick="createValuation"
			/>
		</valuation-layout>
	</div>
</template>

<script>
import ValuationLayout from '@/modules/job-evaluation/components/supplies/valuation-model/commons/ValuationLayout';
import ValuationModelStepper
	from '@/modules/job-evaluation/components/supplies/valuation-model/valuation-model/ValuationModelStepper';
import DegreesDescriptionNavigation
	from '@/modules/job-evaluation/components/supplies/valuation-model/valuation-model/degrees/DegreesDescriptionNavigation';
import DegreeFactorNavigation
	from '@/modules/job-evaluation/components/supplies/valuation-model/valuation-model/degrees/DegreeFactorNavigation';
import DegreeTable
	from '@/modules/job-evaluation/components/supplies/valuation-model/valuation-model/degrees/DegreeTable';
import { mapState } from 'vuex';
import {
	all,
	assoc,
	equals,
	filter,
	head,
	isEmpty,
	length,
	map,
	pathOr,
	pipe,
	prop,
	propEq,
	propOr,
	reverse,
} from 'ramda';
import { assocRate, getPGM, getPGm } from '../../../../utils/valuation-model/degree';

export default {
	name: 'DegreesPage',
	components: {
		DegreeTable,
		DegreeFactorNavigation,
		DegreesDescriptionNavigation,
		ValuationModelStepper,
		ValuationLayout,
	},
	data() {
		return {
			selectedFactorIndex: 0,
		};
	},
	mounted() {
		this.$store.dispatch('jobEvaluation/getDegrees')
			.then(() => {
				this.$store.dispatch('jobEvaluation/updateSelectedFactorDegree', head(this.selectedFactors));
			});
	},
	computed: {
		...mapState('jobEvaluation', {
			selectedFactors: pathOr([], ['supplies', 'valuationModel', 'selectedFactors']),
			selectedFactorDegree: pathOr([], ['supplies', 'valuationModel', 'degree', 'selectedFactor']),
			factorDegrees: pathOr({}, ['supplies', 'valuationModel', 'degree', 'factorDegrees']),
			degreesListByFactor: pathOr({}, ['supplies', 'valuationModel', 'degreesListByFactor']),
			combinations: pathOr([], ['supplies', 'valuationModel', 'weighing', 'combinations']),
		}),
		selectedFactorPGM() {
			return getPGM(this.selectedFactorDegree, this.combinations);
		},
		selectedFactorPGm() {
			return getPGm(this.selectedFactorPGM, this.selectedFactorSelectedDegrees);
		},
		selectedFactorSelectedDegrees() {
			return filter(propEq('selected', true), this.selectedFactorDegrees);
		},
		selectedFactorDegrees() {
			return propOr([], this.selectedFactorDegree.uuid, this.factorDegrees);
		},
		hasNext() {
			return (this.selectedFactorIndex + 1) <= this.selectedFactors.length - 1;
		},
		hasPrev() {
			return (this.selectedFactorIndex - 1) >= 0;
		},
		degreesAreCompleted() {
			const validatedSelected = (factor) => {
				const selected = filter(propEq('selected', true), propOr([], factor.uuid, this.factorDegrees));
				return length(selected) > 0;
			};
			return pipe(
				map(validatedSelected),
				all(equals(true)),
			)(this.selectedFactors);
		},
	},
	methods: {
		initFactorValuation(factor) {
			this.initFactorDegrees(factor);
		},
		initFactorDegrees(factor) {
			const factorDegrees = propOr({}, factor.uuid, this.factorDegrees);
			if (isEmpty(factorDegrees)) {
				this.$store.dispatch(
					'jobEvaluation/updateFactorDegrees',
					assoc(factor.uuid, prop(factor.uuid, this.degreesListByFactor), {}),
				);
			}
		},
		onSelectedFactorDegreesChange(newDegreeList) {
			const parsedList = this.calculateRate(newDegreeList);
			this.$store.dispatch(
				'jobEvaluation/updateFactorDegrees',
				assoc(this.selectedFactorDegree.uuid, parsedList, {}),
			);
		},
		goPrevFactor() {
			this.selectedFactorIndex = this.selectedFactorIndex - 1;
		},
		goNextFactor() {
			this.selectedFactorIndex = this.selectedFactorIndex + 1;
		},
		calculateRate(degreeList) {
			const selectedDegrees = pipe(
				filter(propEq('selected', true)),
				reverse,
			)(degreeList);
			return map(
				degree => assocRate(parseFloat(this.selectedFactorPGM), selectedDegrees, degree),
				degreeList,
			);
		},
		createValuation() {
			this.$store.dispatch('jobEvaluation/createValuation')
				.then(() => {
					this.goTo('job-supply-valuation-model-payroll-proposal');
				});
		},
	},
	watch: {
		selectedFactorDegree: {
			handler(newFactor) {
				this.initFactorValuation(newFactor);
			},
		},
		selectedFactorIndex: {
			handler(newIndex) {
				this.$store.dispatch('jobEvaluation/updateSelectedFactorDegree', prop(newIndex, this.selectedFactors));
			},
		},
	},
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/modules/job-evaluation/pages/supplies/valuation-model/step-pages/DegreesPage.vue